/*
 * Звуки интерфейса: синтез в Web Audio, без файлов.
 *
 * Принципы «тяжёлого» звука: низкий саб с гармониками (слышен и на ноутбучных
 * динамиках), удар + шумовой «треск», затухающий басовый гул с закрывающимся
 * фильтром («ВУУУМ»), общая реверберация, мягкое насыщение и лимитер.
 * Никаких коротких тональных писков: они и дают «8-битное» звучание.
 */
const KEY = "cshub.sfx";

export function sfxEnabled(): boolean {
  try {
    return localStorage.getItem(KEY) !== "0";
  } catch {
    return true;
  }
}
export function setSfxEnabled(on: boolean) {
  try {
    localStorage.setItem(KEY, on ? "1" : "0");
  } catch {
    /* хранилище недоступно: выбор просто не запомнится */
  }
}

/* ───────────── цепочка и примитивы ───────────── */
type Ctx = BaseAudioContext;

function satCurve(k: number): Float32Array {
  const n = 2048;
  const a = new Float32Array(n);
  const d = Math.tanh(k);
  for (let i = 0; i < n; i++) a[i] = Math.tanh(k * ((i * 2) / n - 1)) / d;
  return a;
}

/** Импульс «зала»: затухающий тёмный шум, слегка разный по каналам. */
function impulse(c: Ctx, seconds: number): AudioBuffer {
  const len = Math.floor(c.sampleRate * seconds);
  const buf = c.createBuffer(2, len, c.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    let lp = 0;
    for (let i = 0; i < len; i++) {
      lp += (Math.random() * 2 - 1 - lp) * 0.35;
      d[i] = lp * Math.pow(1 - i / len, 2.6);
    }
  }
  return buf;
}

/** Мастер-цепочка: вход → реверб (параллельно) → насыщение → компрессор → выход. */
function chain(c: Ctx): AudioNode {
  const input = c.createGain();
  const shaper = c.createWaveShaper();
  shaper.curve = satCurve(1.6);
  shaper.oversample = "4x";
  const comp = c.createDynamicsCompressor();
  comp.threshold.value = -12;
  comp.knee.value = 8;
  comp.ratio.value = 10;
  comp.attack.value = 0.001;
  comp.release.value = 0.22;
  const master = c.createGain();
  master.gain.value = 1.15;
  const verb = c.createConvolver();
  verb.buffer = impulse(c, 2.4);
  const wet = c.createGain();
  wet.gain.value = 0.34;
  input.connect(shaper);
  input.connect(verb);
  verb.connect(wet).connect(shaper);
  shaper.connect(comp).connect(master).connect(c.destination);
  return input;
}

let ctx: AudioContext | null = null;
let bus: AudioNode | null = null;

function live(): { c: AudioContext; out: AudioNode } | null {
  if (!sfxEnabled() || typeof window === "undefined") return null;
  const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) {
    ctx = new Ctor();
    bus = chain(ctx);
  }
  if (ctx.state === "suspended") void ctx.resume();
  return { c: ctx, out: bus as AudioNode };
}

/** Огибающая: быстрая атака и экспоненциальный спад. */
function env(g: GainNode, t: number, peak: number, decay: number, attack = 0.004) {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + decay);
}

function noiseSrc(c: Ctx, seconds: number): AudioBufferSourceNode {
  const buf = c.createBuffer(1, Math.ceil(c.sampleRate * seconds), c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  return src;
}

/** Саб: синус падает по высоте и насыщается, поэтому у баса есть слышимые гармоники. */
function sub(c: Ctx, out: AudioNode, t: number, f0: number, f1: number, dur: number, peak: number, drive = 3) {
  const o = c.createOscillator();
  const sh = c.createWaveShaper();
  sh.curve = satCurve(drive);
  const g = c.createGain();
  o.type = "sine";
  o.frequency.setValueAtTime(f0, t);
  o.frequency.exponentialRampToValueAtTime(f1, t + dur * 0.8);
  env(g, t, peak, dur, 0.003);
  o.connect(sh).connect(g).connect(out);
  o.start(t);
  o.stop(t + dur + 0.05);
}

/** Шум через фильтр с «заметанием» частоты. */
function sweepNoise(c: Ctx, out: AudioNode, t: number, dur: number, type: BiquadFilterType, f0: number, f1: number, q: number, peak: number, attack = 0.004, shape: "decay" | "swell" = "decay") {
  const n = noiseSrc(c, dur + 0.05);
  const f = c.createBiquadFilter();
  f.type = type;
  f.Q.value = q;
  f.frequency.setValueAtTime(f0, t);
  f.frequency.exponentialRampToValueAtTime(f1, t + dur);
  const g = c.createGain();
  if (shape === "swell") {
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + dur);
    g.gain.setValueAtTime(0.0001, t + dur + 0.005);
  } else {
    env(g, t, peak, dur, attack);
  }
  n.connect(f).connect(g).connect(out);
  n.start(t);
}

/** Пачка расстроенных пил с фильтром: сначала раскрывается, потом закрывается (гул «ВУУУМ»). */
function drone(c: Ctx, out: AudioNode, t: number, freqs: number[], opts: { rise: number; total: number; peak: number; open: number; close: number; start?: number; type?: OscillatorType; pitchFrom?: number }) {
  const f = c.createBiquadFilter();
  f.type = "lowpass";
  f.Q.value = 2.5;
  const start = opts.start ?? 200;
  f.frequency.setValueAtTime(start, t);
  f.frequency.exponentialRampToValueAtTime(opts.open, t + opts.rise);
  f.frequency.exponentialRampToValueAtTime(opts.close, t + opts.total);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(opts.peak, t + Math.max(opts.rise, 0.01));
  g.gain.exponentialRampToValueAtTime(0.0001, t + opts.total);
  f.connect(g).connect(out);
  freqs.forEach((fr, i) => {
    [-11, 0, 11].forEach((det) => {
      const o = c.createOscillator();
      o.type = opts.type || "sawtooth";
      if (opts.pitchFrom) {
        o.frequency.setValueAtTime(fr * opts.pitchFrom, t);
        o.frequency.exponentialRampToValueAtTime(fr, t + opts.rise);
      } else {
        o.frequency.value = fr;
      }
      o.detune.value = det + i * 1.5;
      o.connect(f);
      o.start(t);
      o.stop(t + opts.total + 0.05);
    });
  });
}

/** Колокол (FM): тёплый и «дорогой», без писка. */
function bell(c: Ctx, out: AudioNode, t: number, freq: number, dur: number, peak: number) {
  const car = c.createOscillator();
  const mod = c.createOscillator();
  const mg = c.createGain();
  const g = c.createGain();
  car.frequency.value = freq;
  mod.frequency.value = freq * 3.5;
  mg.gain.setValueAtTime(freq * 2.2, t);
  mg.gain.exponentialRampToValueAtTime(freq * 0.05, t + dur * 0.7);
  mod.connect(mg).connect(car.frequency);
  env(g, t, peak, dur, 0.002);
  car.connect(g).connect(out);
  car.start(t);
  mod.start(t);
  car.stop(t + dur + 0.05);
  mod.stop(t + dur + 0.05);
}

/** Металлический «лязг»: несколько негармонических синусов. */
function clank(c: Ctx, out: AudioNode, t: number, base: number, peak: number, dur = 0.5) {
  [1, 1.71, 2.62, 3.9].forEach((r, i) => {
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = "sine";
    o.frequency.value = base * r;
    env(g, t, peak / (i + 1), dur / (1 + i * 0.4), 0.001);
    o.connect(g).connect(out);
    o.start(t);
    o.stop(t + dur + 0.05);
  });
}

/** Удар: «кик» + саб + треск + гул шума. */
function impact(c: Ctx, out: AudioNode, t: number, power = 1, tailSec = 1.2) {
  sub(c, out, t, 230, 46, 0.2, 1.0 * power, 2.4);
  sub(c, out, t, 120, 28, tailSec, 1.0 * power, 4.5);
  sweepNoise(c, out, t, 0.12, "highpass", 2600, 1800, 0.7, 0.8 * power, 0.001);
  sweepNoise(c, out, t, tailSec * 0.8, "lowpass", 1400, 110, 0.9, 0.85 * power, 0.002);
}

/* ───────────── сценарии (чистые функции планирования) ───────────── */
function scheduleSearchStart(c: Ctx, out: AudioNode, t: number) {
  impact(c, out, t, 1, 1.5);
  // «ВУУУМ»: гул вспухает за 0.2 с и медленно закрывается вниз.
  drone(c, out, t, [55, 82.41, 110, 164.81, 220], { rise: 0.2, total: 1.7, peak: 0.42, open: 3600, close: 130, pitchFrom: 0.8 });
  // «Воздух» удара: широкий хвост, уходит в реверб.
  sweepNoise(c, out, t + 0.01, 0.9, "highpass", 5500, 3000, 0.5, 0.2, 0.004);
}

function scheduleSearchCancel(c: Ctx, out: AudioNode, t: number) {
  sweepNoise(c, out, t, 0.45, "bandpass", 5200, 140, 1.4, 0.45, 0.01);
  sub(c, out, t, 260, 30, 0.7, 0.9, 3.5);
  drone(c, out, t, [110, 164.81, 220], { rise: 0.04, total: 0.6, peak: 0.28, open: 2400, close: 100, pitchFrom: 1.5 });
  sub(c, out, t + 0.32, 90, 34, 0.5, 0.7, 3);
}

function scheduleMatchFound(c: Ctx, out: AudioNode, t: number) {
  const hitAt = t + 0.45;
  // Нарастание: шум и пилы поднимаются к удару.
  sweepNoise(c, out, t, 0.45, "bandpass", 180, 6500, 0.9, 0.55, 0.01, "swell");
  drone(c, out, t, [73.42, 110, 146.83, 220], { rise: 0.45, total: 0.46, peak: 0.4, open: 4200, close: 4200, start: 160, pitchFrom: 0.6 });
  // Двойной удар и тёмный аккорд, уходящий вниз.
  impact(c, out, hitAt, 1.15, 1.9);
  impact(c, out, hitAt + 0.17, 0.8, 1.2);
  drone(c, out, hitAt, [73.42, 110, 146.83, 174.61, 220, 293.66], { rise: 0.15, total: 2.2, peak: 0.45, open: 4800, close: 160 });
  sweepNoise(c, out, hitAt, 1.1, "highpass", 5000, 2600, 0.5, 0.25, 0.004);
  bell(c, out, hitAt + 0.05, 293.66, 1.6, 0.16);
  bell(c, out, hitAt + 0.2, 440, 1.6, 0.12);
}

function scheduleBan(c: Ctx, out: AudioNode, t: number) {
  impact(c, out, t, 0.9, 0.8);
  clank(c, out, t, 310, 0.5, 0.55);
  sweepNoise(c, out, t, 0.25, "bandpass", 3800, 400, 1.2, 0.35, 0.002);
}

function scheduleYourTurn(c: Ctx, out: AudioNode, t: number) {
  sub(c, out, t, 140, 70, 0.35, 0.45, 2);
  bell(c, out, t, 659.25, 1.1, 0.34);
  bell(c, out, t + 0.12, 987.77, 1.3, 0.3);
  bell(c, out, t + 0.12, 1318.5, 0.9, 0.1);
}

function scheduleTick(c: Ctx, out: AudioNode, t: number) {
  sub(c, out, t, 190, 85, 0.09, 1.0, 2);
  sweepNoise(c, out, t, 0.04, "bandpass", 3200, 2400, 1.5, 0.6, 0.001);
}

function scheduleStep(c: Ctx, out: AudioNode, t: number) {
  sub(c, out, t, 170, 70, 0.28, 0.7, 2.5);
  bell(c, out, t + 0.02, 880, 0.7, 0.14);
}

function scheduleReady(c: Ctx, out: AudioNode, t: number) {
  const hitAt = t + 0.28;
  sweepNoise(c, out, t, 0.28, "bandpass", 300, 7000, 0.9, 0.45, 0.01, "swell");
  drone(c, out, t, [98, 146.83, 196], { rise: 0.28, total: 0.29, peak: 0.34, open: 3800, close: 3800, start: 200, pitchFrom: 0.65 });
  impact(c, out, hitAt, 1.05, 1.7);
  // Тёплый мажорный аккорд (до-соль-до-ми-соль), мягкий и широкий.
  drone(c, out, hitAt, [65.41, 98, 130.81, 164.81, 196], { rise: 0.12, total: 2.0, peak: 0.4, open: 3200, close: 220 });
  [523.25, 659.25, 783.99, 1046.5].forEach((fr, i) => bell(c, out, hitAt + 0.08 + i * 0.09, fr, 1.8, 0.12));
}

/* ───────────── публичный API ───────────── */
/* minGap: защита от двойного срабатывания одного и того же звука (повторный рендер, два источника). */
const run = (fn: (c: Ctx, out: AudioNode, t: number) => void, minGap = 400) => {
  let last = 0;
  return () => {
    const now = performance.now();
    if (now - last < minGap) return;
    const a = live();
    if (!a) return;
    last = now;
    fn(a.c, a.out, a.c.currentTime + 0.005);
  };
};

/** Старт поиска: удар + «ВУУУМ». */
export const playSearchStart = run(scheduleSearchStart);
/** Отмена поиска: гул падает вниз и затухает глухим ударом. */
export const playSearchCancel = run(scheduleSearchCancel);
/** Матч найден: нарастание, двойной удар и тёмный аккорд. */
export const playMatchFound = run(scheduleMatchFound);
/** Бан карты: тяжёлый удар с металлическим лязгом. */
export const playBan = run(scheduleBan);
/** Ваш ход: тёплый колокол. */
export const playYourTurn = run(scheduleYourTurn);
/** Тик последних секунд хода. */
export const playTick = run(scheduleTick, 0);
/** Шаг подготовки сервера. */
export const playStep = run(scheduleStep, 1500);
/** Сервер готов: нарастание, удар и светлый аккорд. */
export const playReady = run(scheduleReady);

/* Прогрев: тяжёлая часть (реверб-импульс, граф) строится при первом касании страницы, а не на клике. */
if (typeof window !== "undefined") {
  const warm = () => {
    window.removeEventListener("pointerdown", warm, true);
    window.removeEventListener("keydown", warm, true);
    const run = () => void live();
    const idle = (window as unknown as { requestIdleCallback?: (f: () => void) => void }).requestIdleCallback;
    if (idle) idle(run);
    else window.setTimeout(run, 0);
  };
  window.addEventListener("pointerdown", warm, true);
  window.addEventListener("keydown", warm, true);
}
